import { useEffect } from 'react';
import classnames from 'classnames';
import css from 'styled-jsx/css';
import PropTypes from 'prop-types';

import { colors } from '../../common/Theme';
import Card from '../../common/layout/Card';
import { HREF as TRAVELERS_HREF } from '../../../../pages/assurance/simulation/voyageurs';
import { formatter } from '@yupwego/string-fns';
import countryCodeOf from '../../../lib/js/countryCodeOf';

import Button from '../../common/elements/Button';

const DESKTOP_BREAKPOINT = 600;

const styles = css`
  .price {
    color: ${colors.ywgPinkishOrange};
    font-size: 3rem;
  }

  .price__decimals {
    font-size: 0.5em;
  }

  .tips {
    padding: 16px 24px;
    color: white;
    background-color: ${colors.ywgPrimary};
    border-radius: 5px;
    box-sizing: border-box;
  }

  .tips-title {
    margin: 0 0 8px;
    padding: 0;
    font-size: 1.875rem;
  }

  .tips-text {
    margin: 0;
    padding: 0;
    font-size: 0.875rem;
  }

  .item {
    margin-bottom: 20px;
  }

  .details-wrapper {
    flex-grow: 1;
  }

  .details,
  .detail-infos {
    margin: 0 0 20px;
    padding: 0;
    list-style: none;
  }

  .detail-infos {
    display: flex;
    font-size: 0.875rem;
    flex-direction: column;
    justify-content: space-between;
  }

  .detail-infos-item {
    margin: 0 0 20px;
  }

  .detail-date-info {
    margin: 0 8px;
  }

  .detail-date-info:last-of-type {
    margin-right: 0;
  }

  .detail-total {
    color: ${colors.ywgPrimary};
  }

  .fflag {
    margin-right: 8px;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    .item {
      margin-bottom: 0;
      width: 50%;
    }

    .detail-infos {
      margin: 0;
      flex-direction: row;
    }

    .detail-infos-item {
      padding: 0 10px;
      text-align: center;
    }

    .detail-infos-item:first-of-type {
      padding-left: 0;
      text-align: left;
    }

    .detail-infos-item:last-of-type {
      text-align: right;
    }

    .detail-country {
      width: 40%;
    }

    .detail-date {
      width: 35%;
    }

    .detail-beneficiaries {
      width: 15%;
    }

    .detail-total {
      width: 10%;
    }
  }

  .u-important {
    font-weight: 700;
  }
`;

const cardStyles = css.resolve`
  .panel {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const Pricing = ({ className, countries, quote }) => {
  const ANCHOR = 'total_price';
  const price = String(quote.total.toFixed(2)).split('.');
  useEffect(() => {
    const top = document.querySelector(`#${ANCHOR}`).offsetTop;
    scrollTo({ top });
  });
  return (
    <div className={classnames('form', className)}>
      <Card
        squared
        className={classnames('panel', className, cardStyles.className)}
      >
        <div className="item">
          <h2 id={ANCHOR} className={classnames('title', className)}>
            Coût total de l’assurance
          </h2>
          <div className="price">
            {price[0]}
            <span className="price__decimals">,{price[1]}</span> €
          </div>
        </div>
        <div className="tips item">
          <h2 className="tips-title">Bon à savoir</h2>
          <p className="tips-text">
            Lorsque vous visitez 2 pays dans la même journée, pensez à bien
            activer votre assurance pour chacun.{' '}
            <strong>
              Vous ne paierez que l’assurance pour le pays qui nécessite
              l’assurance la plus élevée.
            </strong>
          </p>
        </div>
        <div className="details-wrapper">
          <h2>Détails</h2>
          <ol className="details">
            <li className="detail">
              {quote.stepPrices.map(stepPrice => {
                const stay = stepPrice.stay;
                const beneficiariesNumber = stepPrice.invoice.details.length;
                return (
                  <ul key={stay.key} className="detail-infos">
                    <li className="detail-infos-item detail-country">
                      <span
                        className={`fflag ff-md ff-lt fflag-${countryCodeOf(
                          stay.destination,
                          countries
                        )} stay-flag`}
                      />
                      <span className="u-important">{stay.destination}</span>{' '}
                      (zone {stepPrice.invoice.zone})
                    </li>
                    <li className="detail-infos-item detail-date">
                      Du{' '}
                      <span className="detail-date-info u-important">
                        {formatter(stay.startDate)}
                      </span>{' '}
                      au{' '}
                      <span className="detail-date-info u-important">
                        {formatter(stay.endDate)}
                      </span>
                    </li>
                    <li className="detail-infos-item detail-beneficiaries u-important">
                      {beneficiariesNumber} personne
                      {beneficiariesNumber > 1 ? 's' : null}
                    </li>
                    <li className="detail-infos-item detail-total u-important">
                      {String(stepPrice.invoice.total.toFixed(2)).replace(
                        '.',
                        ','
                      )}
                      {' '}€
                    </li>
                  </ul>
                );
              })}
            </li>
          </ol>
          <p>
            La grille de tarifs utilisée de cette simulation est garantie
            jusqu´au {formatter(quote.expiresOn)}
          </p>
          <p>
            Le montant final pourra différer du montant simulé si votre voyage
            change.
          </p>
        </div>
      </Card>
      <div className={classnames('bottom-actions', className)}>
        <a
          href={TRAVELERS_HREF}
          className={classnames('return-link', className)}
        >
          Retour
        </a>
        <Button
          href="/souscrire"
          className={classnames('continue', className)}
          type="button"
          arrow="right"
          shadowed
        >
          Souscrire une assurance
        </Button>
      </div>
      <style jsx>{styles}</style>
      {cardStyles.styles}
    </div>
  );
};

Pricing.propTypes = {
  className: PropTypes.string.isRequired,
  countries: PropTypes.array.isRequired,
  quote: PropTypes.object.isRequired
};

export default Pricing;
